<template>
  <div class="Box">
    <!-- 筛选 条件 -->
    <el-card class="mb5">
      <el-form :model="queryForm.params" label-position="top" size="small">
        <el-row :gutter="20">
          <el-col :span="6">
            <el-form-item :label="$t('podInvalidKeyword.categoryNameOrKeyword')">
              <el-input
                v-model="queryForm.params.keyword"
                :placeholder="$t('podInvalidKeyword.searchPlaceholder')"
                clearable
                style="width: 100%"
                @keyup.enter.native="handleSearch"
              />
            </el-form-item>
          </el-col>
          <el-col :span="4">
            <el-form-item :label="$t('podInvalidKeyword.invalidType')">
              <el-select
                v-model="queryForm.params.keywordType"
                :placeholder="$t('podInvalidKeyword.all')"
                style="width: 100%"
              >
                <el-option :label="$t('podInvalidKeyword.all')" value="" />
                <el-option
                  v-for="item in typeOptions"
                  :key="item.value"
                  :label="item.label"
                  :value="item.value"
                />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="4">
            <el-form-item :label="$t('podInvalidKeyword.status')">
              <el-select
                v-model="queryForm.params.status"
                :placeholder="$t('podInvalidKeyword.all')"
                style="width: 100%"
              >
                <el-option :label="$t('podInvalidKeyword.all')" value="" />
                <el-option
                  v-for="item in statusOptions"
                  :key="item.value"
                  :label="item.label"
                  :value="item.value"
                />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="6">
            <div style="margin-top: 28px">
              <el-button
                icon="el-icon-search" size="small" type="primary"
                @click="handleSearch"
              >{{ $t('podInvalidKeyword.search') }}
              </el-button>
              <el-button
                icon="el-icon-refresh-left" size="small" type="info"
                @click="handleReset"
              >{{ $t('podInvalidKeyword.reset') }}
              </el-button>
            </div>
          </el-col>
        </el-row>
      </el-form>
    </el-card>

    <!-- 列表 -->
    <el-card>
      <div class="table-actions">
        <el-button
          icon="el-icon-plus" size="small" type="primary"
          @click="openDialog()"
        >{{ $t('podInvalidKeyword.addCategory') }}
        </el-button>
        <span class="total-count">
          {{ $t('podInvalidKeyword.total', { total: total }) }}
        </span>
      </div>
      <el-table
        ref="tableRef"
        :data="tableData"
        :header-cell-style="{ background: '#f2f2f2', color: 'black' }"
        border
        style="width: 100%"
      >
        <el-table-column :label="$t('podInvalidKeyword.serialNumber')" align="center" width="70">
          <template slot-scope="scope">
            <span>{{ (queryForm.pageNumber - 1) * queryForm.pageSize + scope.$index + 1 }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('podInvalidKeyword.invalidType')" align="center" width="140">
          <template slot-scope="scope">
            <el-tag :type="scope.row.keywordType === 1 ? 'warning' : 'success'">
              {{ getTypeLabel(scope.row.keywordType) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column
          :label="$t('podInvalidKeyword.nameZh')"
          align="center"
          min-width="150"
          prop="typeLabel"
          show-overflow-tooltip
        />
        <el-table-column
          :label="$t('podInvalidKeyword.namePu')"
          align="center"
          min-width="210"
          prop="typeLabelPu"
          show-overflow-tooltip
        />
        <el-table-column :label="$t('podInvalidKeyword.keywords')" min-width="260">
          <template slot-scope="scope">
            <el-tag
              v-for="(keyword, index) in splitKeywords(scope.row.keywords)"
              :key="index"
              class="keyword-tag"
              effect="plain"
              size="mini"
              type="info"
            >{{ keyword }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column :label="$t('podInvalidKeyword.status')" align="center" width="90">
          <template slot-scope="scope">
            <el-switch
              v-model="scope.row.status"
              :active-value="1"
              :inactive-value="0"
              active-color="#00A551"
              inactive-color="#C0C4CC"
              @change="handleStatusChange(scope.row, $event)"
            />
          </template>
        </el-table-column>
        <el-table-column
          :label="$t('podInvalidKeyword.operation')"
          align="center"
          fixed="right"
          width="130"
        >
          <template slot-scope="scope">
            <el-button size="mini" type="text" @click="openDialog(scope.row)">
              {{ $t('podInvalidKeyword.edit') }}
            </el-button>
            <el-popconfirm
              :title="$t('podInvalidKeyword.deleteConfirm')"
              @confirm="removeItem(scope.row)"
            >
              <el-button
                slot="reference" class="delete-btn" size="mini"
                type="text"
              >{{ $t('podInvalidKeyword.delete') }}
              </el-button>
            </el-popconfirm>
          </template>
        </el-table-column>
      </el-table>
      <!-- 分页 -->
      <el-pagination
        :current-page.sync="queryForm.pageNumber"
        :page-size.sync="queryForm.pageSize"
        :page-sizes="[10, 20, 50, 100]"
        :total="total"
        background
        layout="total, sizes, prev, pager, next, jumper"
        style="margin: 10px 0; float: right"
        @size-change="handleSizeChange"
        @current-change="search"
      />
    </el-card>

    <!-- 新增 / 编辑分类 -->
    <PODInvalidKeywordDialog
      :edit-row="currentRow"
      :visible.sync="dialogVisible"
      @success="search"
    />
  </div>
</template>

<script>
import {
  podInvalidKeywordChangeStatus,
  podInvalidKeywordDelete,
  podInvalidKeywordPage
} from '@/api/resource/podInvalidKeyword';
import PODInvalidKeywordDialog from './component/PODInvalidKeywordDialog';

// 关键词分隔符：换行 / 回车 / 中英文逗号 / 顿号 / 中英文分号 / 竖线（与后端拆分口径保持一致）
const KEYWORD_SEPARATOR = /[\n\r,，、;；|]+/;

export default {
  name: 'PODInvalidKeywordConfig',
  components: { PODInvalidKeywordDialog },
  data() {
    return {
      queryForm: {
        pageNumber: 1,
        pageSize: 10,
        params: {
          keyword: '',
          keywordType: '',
          status: ''
        }
      },
      total: 0,
      tableData: [],
      // 弹窗：编辑时对应的行数据，新增为 null
      currentRow: null,
      dialogVisible: false
    };
  },
  computed: {
    // 不合格类型：1-签名，2-图片
    typeOptions() {
      return [
        { label: this.$t('podInvalidKeyword.typePhoto'), value: 2 },
        { label: this.$t('podInvalidKeyword.typeSignature'), value: 1 }
      ];
    },
    // 状态：1-启用，0-停用
    statusOptions() {
      return [
        { label: this.$t('podInvalidKeyword.enabled'), value: 1 },
        { label: this.$t('podInvalidKeyword.disabled'), value: 0 }
      ];
    }
  },
  created() {
    this.search();
  },
  methods: {
    // 关键词串 -> 数组：按后端同一套分隔符拆分，去空、去重
    splitKeywords(value) {
      if (!value) return [];
      const result = [];
      String(value)
        .split(KEYWORD_SEPARATOR)
        .map((item) => item.trim())
        .filter(Boolean)
        .forEach((item) => {
          const exists = result.some((keyword) => keyword.toLowerCase() === item.toLowerCase());
          if (!exists) result.push(item);
        });
      return result;
    },
    getTypeLabel(keywordType) {
      const target = this.typeOptions.find((item) => item.value === keywordType);
      return target ? target.label : '--';
    },
    buildQueryParams() {
      const { keyword, keywordType, status } = this.queryForm.params;
      const params = {};
      const trimmedKeyword = (keyword || '').trim();
      if (trimmedKeyword) params.keyword = trimmedKeyword;
      if (keywordType !== '' && keywordType !== null && keywordType !== undefined) {
        params.keywordType = keywordType;
      }
      if (status !== '' && status !== null && status !== undefined) {
        params.status = status;
      }
      return {
        pageNumber: this.queryForm.pageNumber,
        pageSize: this.queryForm.pageSize,
        params
      };
    },
    search() {
      podInvalidKeywordPage(this.buildQueryParams()).then((res) => {
        if (res.code === 200) {
          this.tableData = (res.data && res.data.records) || [];
          this.total = (res.data && res.data.total) || 0;
        }
      });
    },
    handleSearch() {
      this.queryForm.pageNumber = 1;
      this.search();
    },
    // 切换每页条数后回到第一页
    handleSizeChange() {
      this.queryForm.pageNumber = 1;
      this.search();
    },
    handleReset() {
      this.queryForm.pageNumber = 1;
      this.queryForm.params = {
        keyword: '',
        keywordType: '',
        status: ''
      };
      this.search();
    },
    // 列表快捷启用 / 停用
    handleStatusChange(row, value) {
      podInvalidKeywordChangeStatus({ id: row.id, status: value })
        .then((res) => {
          if (res.code === 200) {
            this.$message.success(
              value === 1
                ? this.$t('podInvalidKeyword.enabledSuccess')
                : this.$t('podInvalidKeyword.disabledSuccess')
            );
            return;
          }
          row.status = value === 1 ? 0 : 1;
        })
        .catch(() => {
          row.status = value === 1 ? 0 : 1;
        });
    },
    removeItem(row) {
      podInvalidKeywordDelete(row.id).then((res) => {
        if (res.code === 200) {
          this.$message.success(this.$t('podInvalidKeyword.deleteSuccess'));
          if (this.tableData.length === 1 && this.queryForm.pageNumber > 1) {
            this.queryForm.pageNumber -= 1;
          }
          this.search();
        }
      });
    },
    // 打开新增 / 编辑弹窗，row 为空即新增
    openDialog(row) {
      this.currentRow = row || null;
      this.dialogVisible = true;
    }
  }
};
</script>

<style lang="scss" scoped>
.Box {
  box-sizing: border-box;
}

.table-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 15px;

  .total-count {
    color: #909399;
    font-size: 13px;
  }
}

.keyword-tag {
  // 超长关键词限制在单元格宽度内换行，避免撑破表格
  max-width: 100%;
  height: auto;
  margin: 2px 5px 2px 0;
  padding: 0 8px;
  line-height: 20px;
  white-space: normal;
  word-break: break-all;
}

.delete-btn {
  color: #f56c6c;
}
</style>
